<script lang="ts" setup>
import ProductGrayButton from '~/components/products/ProductGrayButton.vue'
import ProductTextStrong from '~/components/products/ProductTextStrong.vue'
import { ProductAttribute, ProductAttributeUtils } from '~/enums/product-attribute'
import type { UnitOfMeasurement } from '~/enums/unit-of-measurement'
import { UnitOfMeasurementUtils } from '~/enums/unit-of-measurement'
import type { ProductAttributeData, ProductData } from '~/types/products'

const props = defineProps<{
  productData: ProductData
}>()

const allProductAttributes = Object.values(ProductAttribute)
const excludedProductAttributes = [
  ProductAttribute.DescriptionHtml,
  ProductAttribute.Type,
  ProductAttribute.Form,
  ProductAttribute.UnitOfMeasurement,
  ProductAttribute.UnitQuantity
]

const visibleAttributes = ref([])
const showAll = ref(false)

const attributesData = computed(() => {
  const { heightInMm, ingredients, lengthInMm, weightInGrams, widthInMm } = props.productData.attributes

  const attributes: ProductAttributeData[] = [
    { id: 'ingredients', name: 'Состав', value: ingredients },
    { id: 'lengthInMm', name: 'Длина, мм', value: lengthInMm },
    { id: 'widthInMm', name: 'Ширина, мм', value: widthInMm },
    { id: 'heightInMm', name: 'Высота, мм', value: heightInMm },
    { id: 'weightInGrams', name: 'Вес, г', value: weightInGrams }
  ].filter((x: ProductAttributeData): boolean => x.value !== null)

  const metaAttributes = props.productData.meta.attributes
  const extraAttributes = []

  let unitOfMeasurement = null
  let unitQuantity = null

  for (const { id, name, value } of metaAttributes) {
    if (allProductAttributes.includes(id as ProductAttribute)) {
      if (!excludedProductAttributes.includes(id as ProductAttribute)) {
        extraAttributes.push({
          id: 'extraAttribute',
          name,
          value: ProductAttributeUtils.formatValue(id as ProductAttribute, value) as string
        })
      }

      if (id === ProductAttribute.UnitOfMeasurement) {
        unitOfMeasurement = value
      } else if (id === ProductAttribute.UnitQuantity) {
        unitQuantity = value
      }
    }
  }

  if (unitOfMeasurement !== null && unitQuantity !== null) {
    attributes.push({
      id: 'unitOfMeasurement',
      name: UnitOfMeasurementUtils.getLabelText(unitOfMeasurement as UnitOfMeasurement),
      value: unitQuantity
    })
  }

  if (extraAttributes.length) {
    attributes.push(...extraAttributes)
  }

  return attributes
})

function formatValue (value: string): string {
  return String(value).replace(/\r\n/g, '\n')
}

function toggleAttributes (): void {
  showAll.value = !showAll.value
  visibleAttributes.value = showAll.value ? attributesData.value : attributesData.value.slice(0, 5)
}

onMounted(() => {
  visibleAttributes.value = attributesData.value.slice(0, 5)
})
</script>

<template>
  <div class="flex flex-col items-stretch md:flex-row">
    <ProductTextStrong class="min-w-full md:min-w-[143px] lg:min-w-[33%]" text="Характеристики" />

    <div class="mt-2 flex flex-col md:mt-0 md:space-y-2">
      <div
        v-for="{ name, value } in visibleAttributes"
        :key="name"
        class="!mb-4 flex flex-col last:!mb-0 md:!mb-6 md:flex-row"
      >
        <dt class="w-full text-sm leading-[143%] text-main-dark opacity-50 md:mr-[1%] md:w-[26%]">
          {{ name }}
        </dt>

        <dd
          class="
            w-full whitespace-pre-wrap text-sm leading-[1.43] tracking-[-0.01em] text-main-dark md:w-[73%]
            md:text-base md:leading-normal
          "
        >
          {{ formatValue(value) }}
        </dd>
      </div>

      <ProductGrayButton v-if="!showAll" :title="'Все характеристики'" @click.prevent="toggleAttributes" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
dd::first-letter {
  text-transform: uppercase;
}
</style>
